import React, { useState, useRef, useContext, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  Form,
  Button,
  Container,
  Row,
  Col,
  Card,
  Alert,
} from "react-bootstrap";
import SimpleHeader from "components/Headers/SimpleHeader";
import { serverHost, createEndpoints } from "variables/serverdata";
import { ThemeContext } from "contexts/ThemeContext.jsx";
import { Input } from "reactstrap";

const DialplanAdd = () => {
  const navigate = useNavigate();
  const { theme } = useContext(ThemeContext);
  const currentSite = JSON.parse(localStorage.getItem("selectedSite"));
  const domain_uuid = currentSite?.domain_uuid || null;
  const isDark = theme === "dark";
  const initialData = {
    name: "",
    condition1_field: "",
    condition1_value: "",
    condition2_field: "",
    condition2_value: "",
    action1: "",
    action2: "",
    context: "",
    order: 200,
    enabled: true,
    description: "",
  };

  const [formData, setFormData] = useState(initialData);
  const initialFormData = useRef(initialData);
  const [error, setError] = useState("");

  // Data lists
  const [extensionList, setExtensionList] = useState([]);
  const [bridgeList, setBridgeList] = useState([]);
  const [voicemailList, setVoicemailList] = useState([]);
  const [flowsList, setFlowsList] = useState([]);
  const [ivrList, setIVRList] = useState([]);
  const [timeConditionsList, setTCList] = useState([]);
  const [destinationsList, setDestList] = useState([]);
  const [ringGroupList, setRingGroup] = useState([]);
  const [domainList, setDomainList] = useState([]);

  const handleChange = (e) => {
    const { name, type, value, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const endpoints = useMemo(
      () =>
        createEndpoints({
          setExtensionList,
          setBridgeList,
          setVoicemailList,
          setFlowsList,
          setIVRList,
          setTCList,
          setDestList,
          setRingGroup,
          setDomainList,
          site_uuid: domain_uuid,
        }),
      []
    );

  useEffect(() => {
    const fetchDomain = async (e) => {
      try {
        const currentSite = JSON.parse(localStorage.getItem("selectedSite"));
        const domain_uuid = currentSite?.domain_uuid || null;
        const response = await fetch(
          `${serverHost}/fetch_domain_name/${domain_uuid}`
        );
        if (!response.ok) throw new Error(`Failed to fetch domain context`);
        const data = await response.json();
        setFormData((prev) => ({ ...prev, context: data.result }));
      } catch (err) {
        setError(err.message);
      }
    };

    const fetchData = async () => {
      for (const key in endpoints) {
        const { url, map, setter } = endpoints[key];
        try {
          const res = await fetch(url);
          if (!res.ok) continue;

          const data = await res.json();
          const mapped = data.results.map(map);
          console.log(key, mapped);

          setter(mapped);
        } catch (err) {
          console.error(`Failed to fetch ${key}:`, err);
        }
      }
    };
    fetchData();
    fetchDomain();
  }, []);

  const handleSave = async () => {
    // Validate required fields
    const requiredFields = [
      "name",
      "condition1_field",
      "condition1_value",
      "order",
    ];
    const missingFields = requiredFields.filter(
      (field) => !formData[field] || formData[field].toString().trim() === ""
    );

    if (missingFields.length > 0) {
      setError(
        `Missing required fields: ${missingFields
          .map((f) => f.replace(/_/g, " "))
          .join(", ")}`
      );
      return;
    }

    const site = JSON.parse(localStorage.getItem("selectedSite"));
    const user = JSON.parse(localStorage.getItem("user"));

    try {
      const payload = {
        data: formData,
        client_uuid: site?.client_uuid,
        insert_uuid: user?.user_uuid,
      };

      const res = await fetch(`${serverHost}/dialplan/${site.domain_uuid}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        throw new Error("Failed to insert dialplan record");
      }

      navigate("/admin/hucs/dialplans");
    } catch (err) {
      console.error(err);
      setError(err.message);
    }
  };

  const handleBack = () => {
    const isDirty = Object.values(formData).some(
      (val) => val !== "" && val !== null
    );

    if (!isDirty || window.confirm("Discard changes and go back?")) {
      navigate(`/admin/hucs/dialplan-routes`);
    }
  };

  const labelStyle = {
    fontWeight: "bolder",
    color: isDark ? "#e2e8f0" : "#1e3a5f",
    marginBottom: "0.5rem",
  };

  return (
    <>
      <SimpleHeader name="Add Dialplan" parentName="HUCS" />

      <Container className="mt--6">
        <Card className="shadow-sm" style={{ marginTop: "-3rem" }}>
          <Card.Header
            style={{
              backgroundColor: isDark ? "#1f2937" : "#e9ecef",
              color: isDark ? "#e2e8f0" : "#1e3a5f",
              borderBottom: "none",
              padding: "1rem 1.5rem",
            }}
          >
            <h4 className="mb-0">ADD DIALPLAN</h4>
          </Card.Header>

          <Card.Body className="p-4">
            {error && <Alert variant="danger">{error}</Alert>}

            <Form
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  handleSave();
                }
              }}
            >
              <Row>
                {/* Name */}
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label style={labelStyle}>
                      Name <span style={{ color: "red" }}>*</span>
                    </Form.Label>
                    <Form.Control
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                    />
                  </Form.Group>
                </Col>

                {/* Context */}
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Context</Form.Label>
                    <Form.Control
                      type="text"
                      name="context"
                      value={formData.context}
                      onChange={handleChange}
                      disabled
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                {/* Condition 1 Field */}
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label style={labelStyle}>
                      Condition 1 Field <span style={{ color: "red" }}>*</span>
                    </Form.Label>
                    <Input
                      type="select"
                      name="condition1_field"
                      value={formData.condition1_field}
                      onChange={handleChange}
                    >
                      <option value=""></option>
                      <optgroup label="Field">
                        <option value="context">Context</option>
                        <option value="username">Username</option>
                        <option value="rdnis">RDNIS</option>
                        <option value="destination_number">
                          Destination Number
                        </option>
                        <option value="public">Public</option>
                        <option value="caller_id_name">Caller ID Name</option>
                        <option value="caller_id_number">
                          Caller ID Number
                        </option>
                        <option value="ani">ANI</option>
                        <option value="ani2">ANI2</option>
                        <option value="uuid">UUID</option>
                        <option value="source">Source</option>
                        <option value="chan_name">Channel Name</option>
                        <option value="network_addr">Network Address</option>
                      </optgroup>
                      <optgroup label="Time">
                        <option value="hour">Hour</option>
                        <option value="minute">Minute</option>
                        <option value="minute-of-day">Minute of Day</option>
                        <option value="mday">Day of Month</option>
                        <option value="mweek">Week of Month</option>
                        <option value="mon">Month</option>
                        <option value="yday">Day of Year</option>
                        <option value="year">Year</option>
                        <option value="wday">Day of Week</option>
                        <option value="week">Week</option>
                      </optgroup>
                    </Input>
                  </Form.Group>
                </Col>

                {/* Condition 1 Value */}
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label style={labelStyle}>
                      Condition 1 Value <span style={{ color: "red" }}>*</span>
                    </Form.Label>
                    <Form.Control
                      type="text"
                      name="condition1_value"
                      value={formData.condition1_value}
                      onChange={handleChange}
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                {/* Condition 2 Field */}
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Condition 2 Field</Form.Label>
                    <Input
                      type="select"
                      name="condition2_field"
                      value={formData.condition2_field}
                      onChange={handleChange}
                    >
                      <option value=""></option>
                      <optgroup label="Field">
                        <option value="context">Context</option>
                        <option value="username">Username</option>
                        <option value="rdnis">RDNIS</option>
                        <option value="destination_number">
                          Destination Number
                        </option>
                        <option value="public">Public</option>
                        <option value="caller_id_name">Caller ID Name</option>
                        <option value="caller_id_number">
                          Caller ID Number
                        </option>
                        <option value="ani">ANI</option>
                        <option value="ani2">ANI2</option>
                        <option value="uuid">UUID</option>
                        <option value="source">Source</option>
                        <option value="chan_name">Channel Name</option>
                        <option value="network_addr">Network Address</option>
                      </optgroup>
                      <optgroup label="Time">
                        <option value="hour">Hour</option>
                        <option value="minute">Minute</option>
                        <option value="minute-of-day">Minute of Day</option>
                        <option value="mday">Day of Month</option>
                        <option value="mweek">Week of Month</option>
                        <option value="mon">Month</option>
                        <option value="yday">Day of Year</option>
                        <option value="year">Year</option>
                        <option value="wday">Day of Week</option>
                        <option value="week">Week</option>
                      </optgroup>
                    </Input>
                  </Form.Group>
                </Col>

                {/* Condition 2 Value */}
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Condition 2 Value</Form.Label>
                    <Form.Control
                      type="text"
                      name="condition2_value"
                      value={formData.condition2_value}
                      onChange={handleChange}
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                {/* Action 1 */}
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label style={labelStyle}>
                      Action 1 <span style={{ color: "red" }}>*</span>{" "}
                    </Form.Label>
                    <Input
                      type="select"
                      name="action1"
                      value={formData.action1}
                      onChange={handleChange}
                    >
                      <option value="">Select...</option>
                      <optgroup label="Bridges">
                        {bridgeList.map((br) => (
                          <option
                            key={br.bridge_uuid}
                            value={`bridge:${br.destination}`}
                          >
                            {br.name}
                          </option>
                        ))}
                      </optgroup>
                      <optgroup label="Call Flows">
                        {flowsList.map((cf) => (
                          <option key={cf.name} value={`transfer:${cf.value}`}>
                            {cf.name}
                          </option>
                        ))}
                      </optgroup>
                      <optgroup label="Destinations">
                        {destinationsList.map((d) => (
                          <option key={d.value} value={`transfer:${d.value}`}>
                            {d.name}
                          </option>
                        ))}
                      </optgroup>
                      <optgroup label="Extensions">
                        {extensionList.map((ext) => (
                          <option
                            key={ext.extension_uuid}
                            value={`transfer:${ext.name} XML ${ext.accountcode}`}
                          >
                            {ext.name}
                          </option>
                        ))}
                      </optgroup>
                      <optgroup label="IVR Menus">
                        {ivrList.map((ivr) => (
                          <option
                            key={ivr.name}
                            value={`transfer:${ivr.transfer}`}
                          >
                            {ivr.name}
                          </option>
                        ))}
                      </optgroup>
                      <optgroup label="Ring Groups">
                        {ringGroupList.map((rg) => (
                          <option key={rg.value} value={`transfer:${rg.value}`}>
                            {rg.name}
                          </option>
                        ))}
                      </optgroup>
                      <optgroup label="Time Conditions">
                        {timeConditionsList.map((tc) => (
                          <option key={tc.name} value={`transfer:${tc.value}`}>
                            {tc.name}
                          </option>
                        ))}
                      </optgroup>
                      <optgroup label="Voicemails">
                        {voicemailList.map((vm) => (
                          <option
                            key={vm.voicemail_uuid}
                            value={`transfer:${vm.value}`}
                          >
                            {vm.voicemail_id}
                          </option>
                        ))}
                      </optgroup>
                    </Input>
                  </Form.Group>
                </Col>

                {/* Action 2 */}
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Action 2</Form.Label>
                    <Input
                      type="select"
                      name="action2"
                      value={formData.action2}
                      onChange={handleChange}
                    >
                      <option value="">Select...</option>
                      <optgroup label="Bridges">
                        {bridgeList.map((br) => (
                          <option
                            key={br.bridge_uuid}
                            value={`bridge:${br.destination}`}
                          >
                            {br.name}
                          </option>
                        ))}
                      </optgroup>
                      <optgroup label="Call Flows">
                        {flowsList.map((cf) => (
                          <option key={cf.name} value={`transfer:${cf.value}`}>
                            {cf.name}
                          </option>
                        ))}
                      </optgroup>
                      <optgroup label="Destinations">
                        {destinationsList.map((d) => (
                          <option key={d.value} value={`transfer:${d.value}`}>
                            {d.name}
                          </option>
                        ))}
                      </optgroup>
                      <optgroup label="Extensions">
                        {extensionList.map((ext) => (
                          <option
                            key={ext.extension_uuid}
                            value={`transfer:${ext.name} XML ${ext.accountcode}`}
                          >
                            {ext.name}
                          </option>
                        ))}
                      </optgroup>
                      <optgroup label="IVR Menus">
                        {ivrList.map((ivr) => (
                          <option
                            key={ivr.name}
                            value={`transfer:${ivr.transfer}`}
                          >
                            {ivr.name}
                          </option>
                        ))}
                      </optgroup>
                      <optgroup label="Ring Groups">
                        {ringGroupList.map((rg) => (
                          <option key={rg.value} value={`transfer:${rg.value}`}>
                            {rg.name}
                          </option>
                        ))}
                      </optgroup>
                      <optgroup label="Time Conditions">
                        {timeConditionsList.map((tc) => (
                          <option key={tc.name} value={`transfer:${tc.value}`}>
                            {tc.name}
                          </option>
                        ))}
                      </optgroup>
                      <optgroup label="Voicemails">
                        {voicemailList.map((vm) => (
                          <option
                            key={vm.voicemail_uuid}
                            value={`transfer:${vm.value}`}
                          >
                            {vm.voicemail_id}
                          </option>
                        ))}
                      </optgroup>
                    </Input>
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                {/* Order */}
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label style={labelStyle}>
                      Order <span style={{ color: "red" }}>*</span>
                    </Form.Label>
                    <Form.Control
                      type="number"
                      name="order"
                      value={formData.order}
                      onChange={handleChange}
                    />
                  </Form.Group>
                </Col>

                {/* Enabled Toggle */}
                <Col md={4}>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "0.75rem",
                      marginBottom: "1.5rem",
                    }}
                  >
                    <span style={{ color: isDark ? "#e2e8f0" : "#495057" }}>
                      Enabled
                    </span>

                    <label className="custom-toggle mb-0">
                      <input
                        type="checkbox"
                        id="enabled"
                        name="enabled"
                        checked={formData.enabled}
                        onChange={handleChange}
                      />
                      <span
                        className="custom-toggle-slider rounded-circle"
                        data-label-off="Off"
                        data-label-on="On"
                      />
                    </label>
                  </div>
                </Col>
              </Row>

              <Row>
                {/* Description */}
                <Col md={12}>
                  <Form.Group className="mb-3">
                    <Form.Label>Description</Form.Label>
                    <Form.Control
                      as="textarea"
                      name="description"
                      rows={3}
                      value={formData.description}
                      onChange={handleChange}
                    />
                  </Form.Group>
                </Col>
              </Row>

              <div className="mt-4 d-flex justify-content-between">
                <Button
                  variant="danger"
                  onClick={handleBack}
                >
                  Cancel
                </Button>

                <Button
                  variant="success"
                  onClick={handleSave}
                  disabled={
                    !formData.name ||
                    !formData.condition1_field ||
                    !formData.condition1_value ||
                    !formData.action1 ||
                    !formData.order
                  }
                >
                  Save
                </Button>
              </div>
            </Form>
          </Card.Body>
        </Card>
      </Container>
    </>
  );
};

export default DialplanAdd;
